import React, { useEffect, useState } from 'react';
import { Grid2 as Grid, Box, Button, useMediaQuery } from '@mui/material';
import { Delete, Edit, Save, PersonAddAlt1, Undo } from '@mui/icons-material';
import {
  doc,
  updateDoc,
  deleteDoc,
  getDoc,
  collection,
  addDoc,
  query,
  where,
  getCountFromServer,
} from 'firebase/firestore';
import { auth, db } from '../../config/firebase';
import {
  Actions,
  useDispatchProvider,
  useStateDialogCustomer,
  useStateFormCustomer,
  useStateFormCustomerPhone,
} from '../utils/StateProvider';
import { useForm } from 'react-hook-form';
import { formatDateTime, getNewDateTime, parseDateTime } from '../../utils/utils';
import SkeletonForm from '../utils/SkeletonForm';
import FieldText from '../fields/FieldText';
import FieldDateTime from '../fields/FieldDateTime';
import FieldTextarea from '../fields/FieldTextArea';
import { createPortal } from 'react-dom';
import { usePermissions } from '../utils/AclProvider';
import FieldCurrency from '../fields/FieldCurrency';

const FormCustomer = ({ portalRef = null }) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const permissions = usePermissions();
  const stateFormCustomer = useStateFormCustomer();
  const stateFormCustomerPhone = useStateFormCustomerPhone();
  const stateDialogCustomer = useStateDialogCustomer();
  const dispatch = useDispatchProvider();
  const { control, getValues, trigger, reset, setValue } = useForm({
    defaultValues: {
      name: '',
      surname: '',
      phone: '',
      email: '',
      taxCode: '',
      sequenceTotal: 0,
      comments: '',
      dateCreated: formatDateTime(getNewDateTime()),
      dateUpdated: '',
      userCreated: '',
      userUpdated: '',
    },
  });

  const [stateForm, setStateForm] = useState('create');
  const [stateLoading, setStateLoading] = useState(true);
  const [stateSaving, setStateSaving] = useState(false);

  const customerDocRef = stateFormCustomer ? doc(db, 'customers', stateFormCustomer) : null;
  const customersDocRef = collection(db, 'customers');

  const fetchCustomer = async () => {
    if (stateFormCustomer === '') {
      reset();
      setStateForm('create');
      return;
    }

    try {
      const docSnap = await getDoc(customerDocRef);
      if (docSnap.exists()) {
        setStateForm('edit');
        // console.log('edit', docSnap.data());
        // setValue(docSnap.data());
        const customerData = docSnap.data();
        setValue('name', customerData.name);
        setValue('surname', customerData.surname);
        setValue('phone', customerData.phone);
        setValue('email', customerData.email);
        setValue('taxCode', customerData.taxCode);
        setValue('sequenceTotal', customerData.sequenceTotal);
        setValue('comments', customerData.comments);
        setValue('dateCreated', formatDateTime(customerData.dateCreated));
        setValue('dateUpdated', formatDateTime(customerData.dateUpdated));
        setValue('userCreated', customerData.userCreated);
        setValue('userUpdated', customerData.userUpdated);
        setStateLoading(false);
      } else {
        dispatch &&
          dispatch({
            type: Actions.Snackbar,
            payload: {
              open: true,
              severity: 'error',
              message: `No customer found!`,
            },
          });
      }
    } catch (err) {
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: {
            open: true,
            severity: 'error',
            message: `Error fetching customer: ${err.message}`,
          },
        });
    }
  };

  const addCustomer = async () => {
    const result = await trigger();
    if (!result) return;
    try {
      setStateSaving(true);
      const customerForm = getValues();
      const newCustomerDocRef = await addDoc(customersDocRef, {
        ...customerForm,
        dateCreated: parseDateTime(customerForm.dateCreated),
        userCreated: auth.currentUser.displayName,
      }).then((customerDocRef) => {
        setStateForm('edit');
        setStateSaving(false);
        if (dispatch) {
          dispatch({
            type: Actions.Snackbar,
            payload: {
              open: true,
              severity: 'success',
              message: `Successfully added customer: ${customerForm.surname} ${customerForm.name}`,
            },
          });
          dispatch({
            type: Actions.FormCustomer,
            payload: customerDocRef.id,
          });
          if (stateDialogCustomer) {
            dispatch({
              type: Actions.DialogCustomer,
              payload: false,
            });
            dispatch({
              type: Actions.FormOrderCustomer,
              payload: customerDocRef.id,
            });
            dispatch({
              type: Actions.FormCustomerPhone,
              payload: '',
            });
            dispatch({
              type: Actions.FormCustomer,
              payload: '',
            });
          }
        }
      });
    } catch (err) {
      setStateSaving(false);
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: {
            open: true,
            severity: 'error',
            message: `Error adding new customer: ${err.message}`,
          },
        });
    }
  };

  const saveCustomer = async () => {
    const result = await trigger();
    if (!result) return;
    try {
      setStateSaving(true);
      const customerForm = getValues();
      delete customerForm.dateCreated;
      delete customerForm.userCreated;
      await updateDoc(customerDocRef, {
        ...customerForm,
        dateUpdated: getNewDateTime(),
        userUpdated: auth.currentUser.displayName,
      }).then(() => {
        setStateSaving(false);
        setStateForm('edit');
        dispatch &&
          dispatch({
            type: Actions.Snackbar,
            payload: {
              open: true,
              severity: 'success',
              message: `Successfully update customer: ${customerForm.surname} ${customerForm.name}`,
            },
          });
      });
    } catch (err) {
      setStateSaving(false);
      dispatch &&
        dispatch({
          type: Actions.Snackbar,
          payload: {
            open: true,
            severity: 'error',
            message: `Error saving new customer: ${err.message}`,
          },
        });
    }
  };

  const editCustomer = () => {
    setStateForm('save');
  };

  const revertOrder = () => {
    setStateForm('edit');
    fetchCustomer().then();
  };

  const handleDialogDelete = () => {
    dispatch &&
      dispatch({
        type: Actions.DialogDelete,
        payload: {
          open: true,
          title: 'Are you sure you want to delete this customer?',
          onConfirm: deleteCustomer,
        },
      });
  };

  const deleteCustomer = async () => {
    const ordersRef = query(collection(db, 'orders'), where('customerRef', '==', customerDocRef));
    const ordersSnapshot = await getCountFromServer(ordersRef);
    if (ordersSnapshot?.data().count === 0) {
      try {
        setStateSaving(true);
        await deleteDoc(customerDocRef).then(() => {
          setStateSaving(false);
          if (dispatch) {
            dispatch({
              type: Actions.DialogDelete,
              payload: {
                open: false,
                title: '',
                onConfirm: () => {},
              },
            });
            dispatch({
              type: Actions.DrawerCustomer,
              payload: false,
            });
            dispatch({
              type: Actions.Snackbar,
              payload: {
                open: true,
                severity: 'success',
                message: 'Successfully customer deleted.',
              },
            });
            dispatch({
              type: Actions.FormCustomer,
              payload: '',
            });
          }
        });
      } catch (err) {
        setStateSaving(false);
        dispatch({
          type: Actions.Snackbar,
          payload: {
            open: true,
            severity: 'error',
            message: `Error deleting customer: ${err}`,
          },
        });
      }
    } else {
      dispatch({
        type: Actions.DialogDelete,
        payload: {
          open: false,
          title: '',
          onConfirm: () => {},
        },
      });
      dispatch({
        type: Actions.Snackbar,
        payload: {
          open: true,
          severity: 'error',
          message: `Please first delete all orders related to this customer.`,
        },
      });
    }
  };

  useEffect(() => {
    setStateLoading(true);
    fetchCustomer().then(() => {
      setStateLoading(false);
    });
  }, [stateFormCustomer]);

  useEffect(() => {
    if (stateForm !== 'create' || stateFormCustomerPhone === '') return;
    setValue('phone', stateFormCustomerPhone);
  }, [stateFormCustomerPhone]);

  return (
    <Box component={'form'}>
      <>
        {stateLoading ? (
          <Grid container>
            <Grid size={{ xs: 12 }}>
              <SkeletonForm />
            </Grid>
          </Grid>
        ) : (
          <Grid
            container
            spacing={3}
          >
            <Grid size={12}>
              <FieldText
                control={control}
                name={'name'}
                label={'Name'}
                rules={[{ required: 'Name is required' }]}
                disabled={stateForm === 'edit'}
              />
            </Grid>
            <Grid size={12}>
              <FieldText
                control={control}
                name={'surname'}
                label={'Surname'}
                rules={[{ required: 'Surname is required' }]}
                disabled={stateForm === 'edit'}
              />
            </Grid>
            <Grid size={12}>
              <FieldText
                control={control}
                name={'phone'}
                label={'Phone'}
                rules={{
                  required: 'Phone is required',
                  minLength: {
                    value: 10,
                    message: 'Phone must be at least 10 characters',
                  },
                  maxLength: {
                    value: 14,
                    message: 'Phone must be at maximum 14 characters',
                  },
                }}
                disabled={stateForm === 'edit'}
              />
            </Grid>
            <Grid size={12}>
              <FieldText
                control={control}
                name={'email'}
                label={'Email'}
                disabled={stateForm === 'edit'}
              />
            </Grid>
            <Grid size={12}>
              <FieldCurrency
                control={control}
                name={'sequenceTotal'}
                label={'Sequence total'}
                disabled={true}
              />
            </Grid>
            <Grid size={12}>
              <FieldText
                control={control}
                name={'taxCode'}
                label={'Tax code'}
                disabled={stateForm === 'edit'}
              />
            </Grid>
            <Grid size={12}>
              <FieldTextarea
                control={control}
                name={'comments'}
                label={'Comments'}
                disabled={stateForm === 'edit'}
              />
            </Grid>
            <Grid size={12}>
              <Grid
                container
                spacing={2}
              >
                <Grid size={6}>
                  <FieldDateTime
                    control={control}
                    name={'dateCreated'}
                    label={'Date Created'}
                    setValue={setValue}
                    disabled={true}
                    disableClear={true}
                    // disabled={stateForm === 'edit'}
                  />
                </Grid>
                <Grid size={6}>
                  <FieldText
                    control={control}
                    name={'userCreated'}
                    label={'User created'}
                    disabled={true}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid size={12}>
              <Grid
                container
                spacing={2}
              >
                <Grid size={6}>
                  <FieldDateTime
                    control={control}
                    name={'dateUpdated'}
                    label={'Date Updated'}
                    setValue={setValue}
                    disabled={true}
                    disableClear={true}
                    // disabled={stateForm === 'edit'}
                  />
                </Grid>
                <Grid size={6}>
                  <FieldText
                    control={control}
                    name={'userUpdated'}
                    label={'User updated'}
                    disabled={true}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
        {portalRef.current &&
          createPortal(
            <>
              <Grid
                container
                spacing={3}
                justifyContent="flex-end"
              >
                {stateForm === 'edit' && permissions?.customers?.update && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'contained'}
                      startIcon={<Edit />}
                      onClick={editCustomer}
                      size={isMobile ? 'small' : 'large'}
                      loading={stateSaving}
                    >
                      Edit
                    </Button>
                  </Grid>
                )}
                {stateForm === 'save' && permissions?.customers?.update && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'text'}
                      startIcon={<Undo />}
                      onClick={revertOrder}
                      size={isMobile ? 'small' : 'large'}
                      disabled={stateSaving}
                    >
                      Undo
                    </Button>
                  </Grid>
                )}
                {stateForm === 'save' && permissions?.customers?.delete && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'text'}
                      startIcon={<Delete />}
                      onClick={handleDialogDelete}
                      size={isMobile ? 'small' : 'large'}
                      disabled={stateSaving}
                    >
                      Delete
                    </Button>
                  </Grid>
                )}
                {stateForm === 'save' && permissions?.customers?.update && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'contained'}
                      startIcon={<Save />}
                      onClick={saveCustomer}
                      size={isMobile ? 'small' : 'large'}
                      loading={stateSaving}
                    >
                      Save
                    </Button>
                  </Grid>
                )}
                {stateForm === 'create' && permissions?.customers?.create && (
                  <Grid size={'auto'}>
                    <Button
                      color={'primary'}
                      variant={'contained'}
                      startIcon={<PersonAddAlt1 />}
                      onClick={addCustomer}
                      size={isMobile ? 'small' : 'large'}
                      loading={stateSaving}
                    >
                      Create
                    </Button>
                  </Grid>
                )}
              </Grid>
            </>,
            portalRef.current,
          )}
      </>
    </Box>
  );
};

export default FormCustomer;
